// Inline-expand variant of the Milestone form — wraps MilestoneFormFields
// in write-mode inside an expanded TrackingTable row. Auto-focuses first
// input on mount; calls `onClosed` on unmount for focus-return.
//
// Single deliverable-relationship surface (post-PR #470): the
// MilestoneDeliverablesPicker is bound to `deliverable.milestoneId` and
// supplies both the "what's linked" view and the editable tick state.
// Ticking sets the FK to this milestone on save; unticking clears it.
// Deliverables already in another milestone render disabled with a
// "currently in <name>" hint.
//
// Save dispatch is dual: the milestone PUT and the linked-deliverables
// bulk-set fire sequentially. If the milestone PUT fails, the linked
// update is skipped (the form stays open showing the error). If the
// milestone PUT succeeds but the linked update fails, the milestone
// fields are persisted and the form surfaces the linked-deliverables
// error inline so the user can retry — the OCC at the deliverable
// layer means a stale retry is safe.
//
// Lifecycle ladder: always-rendered date inputs for deliveredAt,
// partiallyAcceptedAt, acceptedAt, paidAt — gated via disabled attribute
// based on upstream rung state. Ticking the checkbox pre-fills the date
// with Brisbane today; unticking clears.
//
// Cascade confirm: when the server returns 400 CASCADE_REQUIRED (a field
// is cleared while downstream fields are still set), an inline modal opens
// that lists the still-set downstream fields in forward-ladder order.
// Confirming sends a single PATCH that nulls every field in `stillSet`
// plus the originally-cleared field. 409 mid-cascade dismisses the modal
// and shows an inline error prompt to reopen the form.
//
// Null-sending: toCreateMilestoneInput omits falsy lifecycle fields, but
// buildLifecycleNullPatch emits explicit nulls for fields that were set on
// the original item and have been cleared in the form draft — ensuring the
// backend actually removes the timestamp.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Archive as ArchiveIcon } from "lucide-react";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import Modal from "../../../common/Modal";
import MilestoneFormFields from "./MilestoneFormFields";
import MilestoneDeliverablesPicker from "./MilestoneDeliverablesPicker";
import {
  useLinkedDeliverables,
  useSetLinkedDeliverables,
} from "../../../../hooks/useTracking";
import {
  buildMilestoneSuffixUpdatePatch,
  buildSowIdUpdatePatch,
  buildLifecycleNullPatch,
  milestoneNumberInputError,
  stateFromMilestone,
  toCreateMilestoneInput,
  LIFECYCLE_FIELDS,
  type LifecycleField,
  type MilestoneFieldErrors,
  type MilestoneFormState,
} from "./milestoneFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import { deriveAwaitingFullAcceptance } from "../../../../utils/deriveMilestoneStatus";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { ApiError, isConflictError } from "../../../../services/api";
import type {
  Milestone,
  Sow,
  UpdateMilestoneInput,
} from "../../../../types";

function validate(state: MilestoneFormState): MilestoneFieldErrors {
  const errors: MilestoneFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  const numErr = milestoneNumberInputError(state.milestoneNumberInput);
  if (numErr) errors.milestoneNumberInput = numErr;
  if (!isValidIsoDate(state.plannedDate)) {
    errors.plannedDate = "Enter a valid planned date";
  }
  if (state.description.length > 2000) {
    errors.description = "Description must be 2000 characters or fewer";
  }
  if (state.acceptanceCriteria.length > 2000) {
    errors.acceptanceCriteria =
      "Acceptance criteria must be 2000 characters or fewer";
  }
  // Chronological order: each set rung must be >= its upstream rung.
  if (state.deliveredAt && state.partiallyAcceptedAt) {
    if (state.partiallyAcceptedAt < state.deliveredAt) {
      errors.partiallyAcceptedAt =
        "Partially accepted date must be on or after the delivered date";
    }
  }
  if (state.deliveredAt && state.acceptedAt) {
    if (state.acceptedAt < state.deliveredAt) {
      errors.acceptedAt =
        "Accepted date must be on or after the delivered date";
    }
  }
  if (state.partiallyAcceptedAt && state.acceptedAt) {
    if (state.acceptedAt < state.partiallyAcceptedAt) {
      errors.acceptedAt =
        "Accepted date must be on or after the partially accepted date";
    }
  }
  if (state.paidAt) {
    // Payment is anchored to the earliest acceptance rung: partial acceptance
    // when recorded (the carve-out lets a later full acceptance fall after
    // payment), otherwise full acceptance.
    const upstream = state.partiallyAcceptedAt || state.acceptedAt;
    if (upstream && state.paidAt < upstream) {
      errors.paidAt =
        "Paid date must be on or after the partially accepted (or accepted) date";
    }
  }
  return errors;
}

const LIFECYCLE_LABEL: Record<LifecycleField, string> = {
  deliveredAt: "Delivered",
  partiallyAcceptedAt: "Partially accepted",
  acceptedAt: "Accepted",
  paidAt: "Paid",
};

interface LadderRowProps {
  label: string;
  htmlFor: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  onToggle: (checked: boolean) => void;
  checked: boolean;
  error?: string;
  /** Earliest date the user may pick (YYYY-MM-DD). The picker greys out
   *  earlier dates and the browser blocks submit at the HTML level when
   *  the value falls below this floor; the JS validation in `validate()`
   *  is the fallback that produces the inline error text. Omit on the
   *  first rung (deliveredAt) — no upstream anchor exists. */
  min?: string;
}

function LadderRow({
  label,
  htmlFor,
  value,
  disabled,
  onChange,
  onToggle,
  checked,
  error,
  min,
}: LadderRowProps) {
  const checkId = `${htmlFor}-check`;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <label htmlFor={checkId} className="sr-only">{label}</label>
        <input
          type="checkbox"
          id={checkId}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
        />
        <label htmlFor={htmlFor} className="label text-body-sm min-w-[10rem]">
          {label}
        </label>
        <input
          type="date"
          id={htmlFor}
          value={value}
          min={min || undefined}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="input text-body-sm"
          aria-invalid={!!error}
        />
      </div>
      {error && (
        <p className="text-caption text-red-600 dark:text-red-400 pl-7">{error}</p>
      )}
    </div>
  );
}

interface CascadeState {
  /** The field the user originally tried to clear. */
  clearedField: LifecycleField;
  /** Still-set downstream fields returned by the server. */
  stillSet: LifecycleField[];
  /** The full PATCH payload to send on confirm, with all fields nulled. */
  patch: UpdateMilestoneInput;
}

interface MilestoneInlineExpandProps {
  item: Milestone;
  contractId: string;
  /** SOWs on the parent contract. Threaded into `<MilestoneFormFields>`
   *  so the SOW picker can render available options and reflect the
   *  current attachment when editing. */
  sows?: Sow[];
  /** Other milestones on the same contract. Passed to the
   *  MilestoneDeliverablesPicker so cross-milestone deliverables can be
   *  labelled with the owning milestone's name. */
  milestones?: readonly Milestone[];
  onUpdate: (input: UpdateMilestoneInput) => Promise<unknown>;
  onCancel: () => void;
  /** Triggered when the user clicks the destructive Archive button in
   *  the footer. Parent (MilestoneTab) opens its ArchiveDialog to
   *  capture a reason and dispatch the archive mutation. */
  onArchive?: () => void;
  onClosed?: () => void;
  isSubmitting?: boolean;
  /** Optional override for the deliverables picker (test seam). */
  deliverablesSlot?: ReactNode;
}

export default function MilestoneInlineExpand({
  item,
  contractId,
  sows,
  milestones = [],
  onUpdate,
  onCancel,
  onArchive,
  onClosed,
  isSubmitting,
  deliverablesSlot,
}: MilestoneInlineExpandProps) {
  const [state, setState] = useState<MilestoneFormState>(() =>
    stateFromMilestone(item),
  );
  const [errors, setErrors] = useState<MilestoneFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cascadeState, setCascadeState] = useState<CascadeState | null>(null);
  const [isCascading, setIsCascading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const linkedQuery = useLinkedDeliverables(contractId, item.id);
  const setLinkedMutation = useSetLinkedDeliverables();

  // Initialise the picker's ticked set from the live linked-deliverables
  // query result. We let the parent form own the in-progress state so
  // ticking edits before save are visible and reversible. The initial
  // value is computed lazily from the query data once it lands; users
  // see a loading hint via the picker itself until then.
  const [pickerValue, setPickerValue] = useState<string[] | null>(null);
  const [pickerHydrated, setPickerHydrated] = useState(false);
  if (!pickerHydrated && linkedQuery.data) {
    setPickerHydrated(true);
    setPickerValue(linkedQuery.data.map((d) => d.id));
  }

  // Other milestones on the contract for the cross-milestone hint. The
  // current milestone is included; the picker filters its own row out
  // implicitly because that deliverable's milestoneId === item.id and
  // renders as "ticked, enabled", not "in another milestone".
  const otherMilestones = useMemo(() => milestones, [milestones]);

  useEffect(() => {
    const first = rootRef.current?.querySelector<HTMLElement>(
      'input:not([type="checkbox"]):not([type="radio"]), select, textarea',
    );
    first?.focus();
  }, []);

  useEffect(() => {
    return () => {
      onClosed?.();
    };
  }, [onClosed]);

  const handleChange = (patch: Partial<MilestoneFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof MilestoneFieldErrors];
      }
      return next;
    });
  };

  // Detect whether the picker's ticked set diverges from the persisted
  // linked-deliverables set, so we only dispatch the bulk-set on actual
  // change. Treats both sides as unordered sets.
  const pickerIsDirty = (): boolean => {
    if (pickerValue === null) return false;
    const persisted = new Set((linkedQuery.data ?? []).map((d) => d.id));
    const draft = new Set(pickerValue);
    if (persisted.size !== draft.size) return true;
    for (const id of persisted) if (!draft.has(id)) return true;
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      const input: UpdateMilestoneInput = {
        version: item.version,
        ...toCreateMilestoneInput(state, contractId),
        ...buildLifecycleNullPatch(state, item),
        ...buildSowIdUpdatePatch(state, item),
        ...buildMilestoneSuffixUpdatePatch(state, item),
      };
      await onUpdate(input);
      if (pickerIsDirty() && pickerValue !== null) {
        await setLinkedMutation.mutateAsync({
          contractId,
          milestoneId: item.id,
          deliverableIds: pickerValue,
        });
      }
    } catch (err) {
      const parsed = parseCascadeRequiredError(err);
      if (parsed) {
        const { stillSet } = parsed;
        // Build the full null-cascade PATCH now so confirm just fires it.
        const clearedField = detectClearedField(state, item);
        if (clearedField) {
          const nullPatch: Partial<Record<LifecycleField, null>> = {};
          for (const f of stillSet) nullPatch[f] = null;
          nullPatch[clearedField] = null;
          const cascadePatch: UpdateMilestoneInput = {
            version: item.version,
            ...toCreateMilestoneInput(state, contractId),
            ...nullPatch,
            ...buildSowIdUpdatePatch(state, item),
            ...buildMilestoneSuffixUpdatePatch(state, item),
          };
          setCascadeState({ clearedField, stillSet, patch: cascadePatch });
          return;
        }
      }
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save milestone.",
      );
    }
  };

  const handleCascadeConfirm = async () => {
    if (!cascadeState) return;
    setIsCascading(true);
    setSubmitError(null);
    try {
      await onUpdate(cascadeState.patch);
      setCascadeState(null);
    } catch (err) {
      setCascadeState(null);
      if (isConflictError(err)) {
        setSubmitError(
          "Another user updated this milestone. Reopen to retry.",
        );
      } else {
        setSubmitError(
          err instanceof Error ? err.message : "Failed to save milestone.",
        );
      }
    } finally {
      setIsCascading(false);
    }
  };

  // Derived disabled gates for each rung.
  const deliveredFilled = !!state.deliveredAt;
  const acceptanceFilled = !!(state.acceptedAt || state.partiallyAcceptedAt);

  // Lexicographic max for YYYY-MM-DD strings — used to compute the
  // strictest applicable min-floor for each picker. Empty strings sort
  // first so they're naturally ignored when a sibling rung is set.
  const maxDate = (...dates: string[]): string | undefined => {
    const filled = dates.filter((d) => !!d);
    if (filled.length === 0) return undefined;
    return filled.reduce((a, b) => (a > b ? a : b));
  };

  const awaitingAcceptance = deriveAwaitingFullAcceptance({
    partiallyAcceptedAt: state.partiallyAcceptedAt || undefined,
    acceptedAt: state.acceptedAt || undefined,
  });

  const idPrefix = `ms-inline-${item.id}`;

  return (
    <div ref={rootRef} data-testid="milestone-inline-expand">
      <form onSubmit={handleSubmit} className="space-y-4">
        <MilestoneFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={idPrefix}
          disabled={isSubmitting}
          sows={sows}
          deliverablesSlot={
            deliverablesSlot ?? (
              <MilestoneDeliverablesPicker
                contractId={contractId}
                milestoneId={item.id}
                milestones={otherMilestones}
                value={pickerValue ?? []}
                onChange={setPickerValue}
                disabled={isSubmitting || linkedQuery.isPending}
              />
            )
          }
        />

        {/* Lifecycle ladder */}
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
          <p className="text-caption font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Lifecycle dates
          </p>
          <LadderRow
            label="Delivered"
            htmlFor={`${idPrefix}-deliveredAt`}
            value={state.deliveredAt}
            disabled={!!isSubmitting}
            checked={!!state.deliveredAt}
            onChange={(v) => handleChange({ deliveredAt: v })}
            onToggle={(checked) => {
              if (checked) {
                handleChange({ deliveredAt: getBrisbaneToday() });
              } else {
                handleChange({ deliveredAt: "" });
              }
            }}
          />
          <LadderRow
            label="Partially accepted"
            htmlFor={`${idPrefix}-partiallyAcceptedAt`}
            value={state.partiallyAcceptedAt}
            disabled={!!isSubmitting || !deliveredFilled}
            checked={!!state.partiallyAcceptedAt}
            min={state.deliveredAt || undefined}
            onChange={(v) => handleChange({ partiallyAcceptedAt: v })}
            onToggle={(checked) => {
              if (checked) {
                handleChange({ partiallyAcceptedAt: getBrisbaneToday() });
              } else {
                handleChange({ partiallyAcceptedAt: "" });
              }
            }}
            error={errors.partiallyAcceptedAt}
          />
          <LadderRow
            label="Accepted on"
            htmlFor={`${idPrefix}-acceptedAt`}
            value={state.acceptedAt}
            disabled={!!isSubmitting || !deliveredFilled}
            checked={!!state.acceptedAt}
            // Accepted must be ≥ both deliveredAt AND partiallyAcceptedAt
            // (when both are set). Take the strictest floor.
            min={maxDate(state.deliveredAt, state.partiallyAcceptedAt)}
            onChange={(v) => handleChange({ acceptedAt: v })}
            onToggle={(checked) => {
              if (checked) {
                handleChange({ acceptedAt: getBrisbaneToday() });
              } else {
                handleChange({ acceptedAt: "" });
              }
            }}
            error={errors.acceptedAt}
          />
          <LadderRow
            label="Paid on"
            htmlFor={`${idPrefix}-paidAt`}
            value={state.paidAt}
            disabled={!!isSubmitting || !acceptanceFilled}
            checked={!!state.paidAt}
            // Paid is anchored to the earliest acceptance rung: partial
            // acceptance when set (the carve-out lets a later full acceptance
            // fall after payment), otherwise full acceptance. Mirrors the JS
            // validation floor above.
            min={state.partiallyAcceptedAt || state.acceptedAt || undefined}
            onChange={(v) => handleChange({ paidAt: v })}
            onToggle={(checked) => {
              if (checked) {
                handleChange({ paidAt: getBrisbaneToday() });
              } else {
                handleChange({ paidAt: "" });
              }
            }}
            error={errors.paidAt}
          />
        </div>

        {awaitingAcceptance && (
          <div
            data-testid="awaiting-acceptance-callout"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-caption text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-300"
          >
            Awaiting full acceptance — the supplier still owes the rejected
            portion from the partial acceptance recorded above.
          </div>
        )}

        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex items-center justify-between gap-3">
          {onArchive && !item.archivedAt ? (
            <Button
              variant="ghost"
              type="button"
              icon={<ArchiveIcon className="h-4 w-4" />}
              onClick={onArchive}
              disabled={isSubmitting}
              aria-label={`Archive ${item.name}`}
            >
              Archive milestone
            </Button>
          ) : (
            <span aria-hidden="true" />
          )}
          <div className="flex gap-3">
            <Button
              variant="ghost"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Save changes
            </Button>
          </div>
        </div>
      </form>

      {/* Cascade confirm dialog — opens when server returns CASCADE_REQUIRED */}
      {cascadeState && (
        <Modal
          isOpen
          onClose={() => setCascadeState(null)}
          title={`Revert milestone to ${cascadeConfirmTitle(cascadeState.clearedField)}?`}
          maxWidth="sm:max-w-md"
        >
          <div
            data-testid="cascade-confirm-dialog"
            className="space-y-4"
          >
            <p className="text-body text-slate-700 dark:text-slate-300">
              Clearing{" "}
              <strong>{LIFECYCLE_LABEL[cascadeState.clearedField]}</strong> will
              also clear the following downstream fields:
            </p>
            <ul
              className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50"
              aria-label="Fields that will be cleared"
            >
              {cascadeState.stillSet.map((field) => (
                <li
                  key={field}
                  className="text-body-sm text-slate-700 dark:text-slate-300"
                >
                  {LIFECYCLE_LABEL[field]}
                  {item[field] ? (
                    <span className="ml-2 text-caption text-slate-500 dark:text-slate-400">
                      ({formatDayMonthYear(item[field] as string)})
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setCascadeState(null)}
                disabled={isCascading}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                type="button"
                loading={isCascading}
                disabled={isCascading}
                onClick={handleCascadeConfirm}
              >
                {cascadeConfirmLabel(cascadeState.clearedField)}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CascadeRequiredPayload {
  stillSet: LifecycleField[];
}

function parseCascadeRequiredError(
  err: unknown,
): CascadeRequiredPayload | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== "CASCADE_REQUIRED") return null;
  const stillSet = (err.payload as { stillSet?: unknown } | undefined)?.stillSet;
  if (!Array.isArray(stillSet)) return null;
  const valid = stillSet.filter(
    (f): f is LifecycleField =>
      LIFECYCLE_FIELDS.includes(f as LifecycleField),
  );
  if (valid.length === 0) return null;
  return { stillSet: valid };
}


/** Find which lifecycle field was cleared in the current draft vs. the item. */
function detectClearedField(
  state: MilestoneFormState,
  item: Milestone,
): LifecycleField | null {
  for (const field of LIFECYCLE_FIELDS) {
    if (item[field] && !state[field]) return field;
  }
  return null;
}

function cascadeConfirmTitle(clearedField: LifecycleField): string {
  switch (clearedField) {
    case "deliveredAt":
      return "upcoming";
    case "partiallyAcceptedAt":
      return "delivered";
    case "acceptedAt":
      return "delivered";
    case "paidAt":
      return "accepted or partially accepted";
  }
}

function cascadeConfirmLabel(clearedField: LifecycleField): string {
  switch (clearedField) {
    case "deliveredAt":
      return "Revert to upcoming";
    case "partiallyAcceptedAt":
      return "Revert to delivered";
    case "acceptedAt":
      return "Revert to delivered";
    case "paidAt":
      return "Clear paid";
  }
}
